import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { webApi } from '@/services/webApi'
import { normalizeProperty } from '@/lib/apiNormalize'
import type { GroupedSuggestions, SuggestionItem } from '@/types/api'
import type { Property } from '@/types/property'

const EMPTY_SUGGESTIONS: GroupedSuggestions = {
  cities: [],
  localities: [],
  areas: [],
  projects: [],
  builders: [],
  properties: [],
  recent: [],
  trending: [],
}

/** Debounce any fast-changing value (used for the search box). */
export function useDebouncedValue<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])

  return debounced
}

/** Hero search tab scope passed to the suggestions API. */
export interface SearchTabScope {
  intent: string
  premium?: boolean
  category?: string
}

/**
 * Grouped type-ahead suggestions from the Smart Search API.
 * Debounced; only fires once the trimmed query has at least 2 characters.
 * When empty, the API still returns recent + trending so callers can show a
 * useful default panel.
 */
export function useSearchSuggestions(query: string, city?: string, tab?: SearchTabScope) {
  const debounced = useDebouncedValue(query.trim(), 250)
  const enabled = debounced.length >= 2

  const suggestionsQuery = useQuery({
    queryKey: [
      'web',
      'search',
      'suggestions',
      debounced,
      city ?? null,
      tab?.intent ?? null,
      tab?.premium ?? null,
      tab?.category ?? null,
    ],
    queryFn: () =>
      webApi.searchSuggestions({
        q: debounced,
        city,
        intent: tab?.intent,
        premium: tab?.premium,
        category: tab?.category,
        limit: 10,
      }),
    enabled,
    staleTime: 60_000,
  })

  const data = suggestionsQuery.data ?? EMPTY_SUGGESTIONS

  // Flatten in display priority: property > project > locality > area > builder > city.
  const flat = useMemo<SuggestionItem[]>(
    () => [
      ...data.properties,
      ...data.projects,
      ...data.localities,
      ...data.areas,
      ...data.builders,
      ...data.cities,
    ],
    [data],
  )

  return {
    grouped: data,
    flat,
    isLoading: enabled && suggestionsQuery.isLoading,
    isEnabled: enabled,
    isFetched: suggestionsQuery.isFetched,
  }
}

/** Trending searches (server analytics with locality cold-start fallback). */
export function useTrendingSearches(city?: string) {
  const query = useQuery({
    queryKey: ['web', 'search', 'trending', city ?? null],
    queryFn: () => webApi.searchTrending({ city, limit: 8 }),
    staleTime: 15 * 60_000,
  })

  return { trending: query.data ?? [], isLoading: query.isLoading }
}

/** Per-user recent searches (requires auth token; empty otherwise). */
export function useRecentSearches(enabled = true) {
  const query = useQuery({
    queryKey: ['web', 'search', 'recent'],
    queryFn: () => webApi.searchRecent({ limit: 8 }),
    enabled,
    staleTime: 60_000,
  })

  return { recent: query.data ?? [], isLoading: query.isLoading }
}

/**
 * Idle search panel (empty query): server recent + trending for the active city/tab.
 */
export function useSearchIdleSuggestions(city?: string, tab?: SearchTabScope, enabled = true) {
  const query = useQuery({
    queryKey: [
      'web',
      'search',
      'idle',
      city ?? null,
      tab?.intent ?? null,
      tab?.premium ?? null,
      tab?.category ?? null,
    ],
    queryFn: () =>
      webApi.searchSuggestions({
        q: '',
        city,
        intent: tab?.intent,
        premium: tab?.premium,
        category: tab?.category,
        limit: 10,
      }),
    enabled,
    staleTime: 5 * 60_000,
  })

  return {
    grouped: query.data ?? EMPTY_SUGGESTIONS,
    isLoading: query.isLoading,
  }
}

export interface NearbySeed {
  lat?: number | null
  lng?: number | null
  radiusKm?: number
  perPage?: number
  excludeId?: number
  builder?: string
  category?: number
}

/** Nearby properties ranked by distance (homepage + property detail). */
export function useNearbyProperties(seed?: NearbySeed): Property[] {
  const hasCoords = seed?.lat != null && seed?.lng != null

  const query = useQuery({
    queryKey: ['web', 'properties', 'nearby', seed],
    queryFn: () =>
      webApi.nearbyProperties({
        lat: seed!.lat as number,
        lng: seed!.lng as number,
        radiusKm: seed?.radiusKm ?? 25,
        perPage: seed?.perPage ?? 8,
        excludeId: seed?.excludeId,
        builder: seed?.builder,
        category: seed?.category,
      }),
    enabled: hasCoords,
    staleTime: 5 * 60_000,
  })

  return (query.data?.items ?? []).map(normalizeProperty)
}
